import { sv } from "date-fns/locale";
import ReactDatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Path,
  Controller,
  useFormContext,
  useController,
} from "react-hook-form";

import { ModalLabel } from "components/ModalLabel";
import { twMerge } from "tailwind-merge";
import { getDay } from "date-fns";
import { forwardRef, InputHTMLAttributes } from "react";

registerLocale("sv", sv);

type FormDataType<T = any> = { [key: string]: T };

type Props<T extends FormDataType> =
  React.InputHTMLAttributes<HTMLInputElement> & {
    name: Path<T>;
    label?: string;
    // required?: boolean;
    placeholder?: string;
    maxDate?: Date;
    labelClassName?: string;
    calendarIconClassName?: string;
    minDate?: Date;
    customType?: "day" | "week" | "month";
    inline?: boolean;
    excludeWeekends?: boolean;
    showTimeInput?: boolean;
    useCustomInput?: boolean;
    customValue?: string;
    dateFormat?: string;
    isClearable?: boolean;
  };

// Add explicit types
interface CustomInputProps extends InputHTMLAttributes<HTMLInputElement> {
  onClick?: () => void;
  value?: string | number | readonly string[];
  placeholder?: string;
}

export function DatePickerNew<T extends FormDataType>(props: Props<T>) {
  const {
    name,
    label,
    // required,
    placeholder,
    maxDate,
    labelClassName,
    minDate,
    disabled,
    customType,
    inline,
    excludeWeekends,
    showTimeInput,
    required,
    useCustomInput,
    customValue,
    dateFormat,
    isClearable = true,
  } = props;

  const {
    control,
    // formState: { errors },
    clearErrors,
  } = useFormContext<T>();

  const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
    ({ onClick, placeholder, disabled }, ref) => (
      <input
        className={
          "cursor-pointer rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:w-[464px] sm:text-sm"
        }
        onClick={onClick}
        ref={ref}
        value={customValue} // Your custom text here
        placeholder={placeholder}
        disabled={disabled}
        readOnly
      />
    ),
  );

  const {
    field: { onBlur },
    fieldState: { error },
  } = useController({ control, name });

  const isMonthPicker = customType === "month";
  const isWeekPicker = customType === "week";

  const isWeekday = (date: Date) => {
    const day = getDay(date);
    return day !== 0 && day !== 6;
  };

  return (
    <div className="relative flex flex-col">
      {!!label && (
        <ModalLabel
          className={twMerge(
            "mb-1",
            labelClassName,
            disabled ? "text-gray-400" : "",
          )}
          required={required}
        >
          {label}
        </ModalLabel>
      )}

      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <>
            {/* <div className="relative h-0 w-0"> */}
            {/* <CalendarDaysIcon className="h-5 w-5 absolute left-2 top-8 z-50 text-gray-500" /> */}
            {/* </div> */}
            <ReactDatePicker
              onChange={(date) => {
                field.onChange(date);
                if (error) {
                  clearErrors(name);
                }
              }}
              onBlur={onBlur}
              selected={field.value}
              dateFormat={
                !!dateFormat
                  ? dateFormat
                  : isMonthPicker
                    ? "yyyy-MM"
                    : showTimeInput
                      ? "yyyy-MM-dd HH:mm"
                      : "yyyy-MM-dd"
              }
              showMonthYearPicker={isMonthPicker}
              showFullMonthYearPicker={isMonthPicker}
              showWeekPicker={isWeekPicker}
              locale={"sv"}
              placeholderText={placeholder}
              isClearable={isClearable}
              disabled={disabled}
              inline={inline}
              maxDate={maxDate}
              filterDate={excludeWeekends ? isWeekday : undefined}
              minDate={minDate}
              showTimeInput={showTimeInput}
              wrapperClassName="w-fit"
              className={twMerge(
                "cursor-pointer rounded-md shadow-sm disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200 sm:w-96 sm:text-sm",
                error
                  ? "border-red-300 pr-10 text-red-900 focus:border-red-500 focus:outline-none focus:ring-red-500"
                  : "border-gray-300 focus:border-cyan-500 focus:ring-cyan-500",
              )}
              customInput={useCustomInput ? <CustomInput /> : undefined}
            />
          </>
        )}
      />
      {error && <p className="mt-2 text-sm text-red-600">{error.message}</p>}
    </div>
  );
}
